import {
  Button,
  Card,
  Dialog,
  EmptyState,
  Select,
  SkeletonText,
  SkeletonTable,
} from '@aurora/ui-kit';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  FileDown,
  FolderKanban,
  Gauge,
  Info,
  OctagonAlert,
  Package,
  TrendingUp,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { api } from '../api/client.js';
import { tenantStore } from '../state/tenant.js';

type Format = 'money' | 'number' | 'percent' | 'hours' | 'days';
type Domain = 'finance' | 'sales' | 'projects' | 'hr' | 'inventory';
type Severity = 'critical' | 'warning' | 'info' | 'ok';

interface DrilldownFilter {
  field: string;
  op: string;
  value?: string | number | boolean;
  values?: (string | number)[];
}
interface Drilldown {
  source: string;
  filters?: DrilldownFilter[];
}
interface Kpi {
  key: string;
  label: string;
  value: number;
  format: Format;
  deltaPercent?: number;
  tone?: 'good' | 'bad' | 'neutral';
  series?: number[];
  hero?: boolean;
  hint?: string;
  drilldown?: Drilldown;
}

interface ResultColumn {
  key: string;
  label: string;
  type: 'text' | 'number' | 'money' | 'date' | 'boolean';
  dimension: boolean;
}
interface RowsResult {
  columns: ResultColumn[];
  rows: Record<string, unknown>[];
  truncated: boolean;
}
interface Series {
  label: string;
  color?: string;
  points: number[];
}
interface Chart {
  key: string;
  title: string;
  format: Format;
  kind: 'bars' | 'lines';
  series: Series[];
  /** Eigene X-Achse (z. B. Kategorien) statt der Monate. */
  labels?: string[];
}
interface Insight {
  severity: Severity;
  title: string;
  detail: string;
  action?: { label: string; to: string };
}
interface Section {
  domain: Domain;
  key: string;
  title: string;
  kpis: Kpi[];
  charts?: Chart[];
  insights?: Insight[];
}
interface ControllingResponse {
  months: string[];
  today: string;
  sections: Section[];
}

const SERIES_COLORS: Record<string, string> = {
  gray: 'var(--au-text-faint)',
  blue: '#3b82f6',
  green: '#16a34a',
  yellow: '#d4a72c',
  orange: '#ea7317',
  red: '#dc2626',
  purple: '#9333ea',
  teal: '#0d9488',
  pink: '#db2777',
};

const DOMAIN_META: Record<Domain, { label: string; icon: typeof Wallet }> = {
  finance: { label: 'Finanzen', icon: Wallet },
  sales: { label: 'Vertrieb', icon: TrendingUp },
  projects: { label: 'Projekte', icon: FolderKanban },
  hr: { label: 'Personal', icon: Users },
  inventory: { label: 'Lager', icon: Package },
};

const SEVERITY_META: Record<
  Severity,
  { label: string; icon: typeof Info; className: string; order: number }
> = {
  critical: { label: 'Kritisch', icon: OctagonAlert, className: 'ctl-insight--critical', order: 0 },
  warning: { label: 'Achtung', icon: AlertTriangle, className: 'ctl-insight--warning', order: 1 },
  info: { label: 'Hinweis', icon: Info, className: 'ctl-insight--info', order: 2 },
  ok: { label: 'In Ordnung', icon: CheckCircle2, className: 'ctl-insight--ok', order: 3 },
};

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];

function monthShort(ym: string): string {
  return MONTH_SHORT[Number(ym.slice(5, 7)) - 1] ?? ym;
}

function formatValue(value: number, format: Format, compact = false): string {
  switch (format) {
    case 'money': {
      const euros = value / 100;
      if (compact && Math.abs(euros) >= 10_000) {
        return `${(euros / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} T€`;
      }
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: Math.abs(euros) >= 1000 ? 0 : 2,
      }).format(euros);
    }
    case 'percent':
      return `${value.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
    case 'hours':
      return `${value.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h`;
    case 'days':
      return `${Math.round(value)} Tage`;
    case 'number':
    default:
      return value.toLocaleString('de-DE');
  }
}

/** Mini-Trendlinie in der KPI-Kachel — normalisiert min..max, mit Flächenfüllung. */
function Sparkline({ points, tone }: { points: number[]; tone?: Kpi['tone'] }) {
  if (points.length < 2) return null;
  const w = 120;
  const h = 34;
  const min = Math.min(...points, 0);
  const max = Math.max(...points, 0);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const y = (v: number): number => h - 3 - ((v - min) / span) * (h - 6);
  const path = points
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');
  const color =
    tone === 'bad'
      ? SERIES_COLORS.red!
      : tone === 'good'
        ? SERIES_COLORS.green!
        : 'var(--au-accent, #3b82f6)';
  return (
    <svg className="ctl-spark" viewBox={`0 0 ${w} ${h}`} aria-hidden preserveAspectRatio="none">
      <path
        d={`${path} L${w},${h} L0,${h} Z`}
        fill="currentColor"
        opacity="0.09"
        style={{ color }}
      />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function Delta({ kpi }: { kpi: Kpi }) {
  if (kpi.deltaPercent === undefined) return null;
  const up = kpi.deltaPercent >= 0;
  const cls = kpi.tone === 'good' ? 'ctl-delta--good' : kpi.tone === 'bad' ? 'ctl-delta--bad' : '';
  return (
    <span className={`ctl-delta ${cls}`}>
      {up ? '▲' : '▼'}{' '}
      {Math.abs(kpi.deltaPercent).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %
    </span>
  );
}

function HeroCard({ kpi, onDrill }: { kpi: Kpi; onDrill: (kpi: Kpi) => void }) {
  const clickable = kpi.drilldown !== undefined;
  return (
    <div
      className={`ctl-hero-card${clickable ? ' ctl-hero-card--clickable' : ''}`}
      {...(clickable
        ? {
            role: 'button' as const,
            tabIndex: 0,
            title: 'Datensätze anzeigen',
            onClick: () => onDrill(kpi),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') onDrill(kpi);
            },
          }
        : {})}
    >
      <div className="ctl-hero-card__label">
        {kpi.label}
        {clickable ? <span className="ctl-drill-hint">↳</span> : null}
      </div>
      <div
        className={`ctl-hero-card__value${kpi.format === 'money' && kpi.value < 0 ? ' ctl-hero-card__value--neg' : ''}`}
      >
        {formatValue(kpi.value, kpi.format)}
      </div>
      <div className="ctl-hero-card__meta">
        <Delta kpi={kpi} />
        {kpi.hint ? <span className="ctl-hero-card__hint">{kpi.hint}</span> : null}
      </div>
      {kpi.series ? <Sparkline points={kpi.series} tone={kpi.tone} /> : null}
    </div>
  );
}

/** Text-Zelle robust normalisieren (Engine liefert Strings/Zahlen). */
function cellText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value) ?? '';
}

/** Analytics-Zelle je Typ formatieren (Cent → €, Datum, Status …). */
function formatCell(column: ResultColumn, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (column.type === 'money') return formatValue(Number(value), 'money');
  if (column.type === 'number') return Number(value).toLocaleString('de-DE');
  if (column.type === 'date') {
    const text = cellText(value);
    const [y, m, d] = text.split('-');
    return d !== undefined ? `${d}.${m}.${y}` : text;
  }
  if (column.type === 'boolean') return value === true ? 'ja' : 'nein';
  return cellText(value);
}

/**
 * Gruppierte Balken (bis 2 Serien) — div-basiert, gestochen scharf.
 * X-Achse sind normalerweise die Monate; liefert der Provider eigene
 * `labels` (z. B. Kostenkategorien), richten sich die Punkte daran aus.
 */
function BarsChart({ chart, months }: { chart: Chart; months: string[] }) {
  const axis = chart.labels ?? months.map(monthShort);
  const all = chart.series.flatMap((s) => s.points);
  const max = Math.max(...all, 1);
  const wide = axis.length <= 8;
  return (
    <div className="ctl-chart">
      <div className="ctl-chart__head">
        <span className="ctl-chart__title">{chart.title}</span>
        <span className="ctl-chart__legend">
          {chart.series.map((s) => (
            <span key={s.label} className="ctl-chart__legend-item">
              <i style={{ background: SERIES_COLORS[s.color ?? 'blue'] }} /> {s.label}
            </span>
          ))}
        </span>
      </div>
      <div className="ctl-bars" role="img" aria-label={chart.title}>
        {axis.map((label, index) => (
          <div key={label} className="ctl-bars__month">
            <div className="ctl-bars__group">
              {chart.series.map((s) => {
                const value = s.points[index] ?? 0;
                return (
                  <div
                    key={s.label}
                    className={`ctl-bars__bar${wide ? ' ctl-bars__bar--wide' : ''}`}
                    title={`${s.label} ${label}: ${formatValue(value, chart.format)}`}
                    style={{
                      height: `${Math.max(value > 0 ? 3 : 0, (value / max) * 100)}%`,
                      background: SERIES_COLORS[s.color ?? 'blue'],
                    }}
                  />
                );
              })}
            </div>
            <span className="ctl-bars__label" title={label}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Linien-Chart mit Nulllinie — verträgt negative Werte (Gewinn/Verlust,
 * Liquiditätslücke). X-Achse sind die Monate, sofern der Provider keine
 * eigenen `labels` (z. B. Kalenderwochen) mitgibt.
 */
function LinesChart({ chart, months }: { chart: Chart; months: string[] }) {
  const axis = chart.labels ?? months.map(monthShort);
  const w = 560;
  const h = 150;
  const pad = 8;
  const all = chart.series.flatMap((s) => s.points);
  const min = Math.min(...all, 0);
  const max = Math.max(...all, 0);
  const span = max - min || 1;
  const step = (w - pad * 2) / Math.max(axis.length - 1, 1);
  const y = (v: number): number => h - pad - ((v - min) / span) * (h - pad * 2);
  const zeroY = y(0);
  return (
    <div className="ctl-chart">
      <div className="ctl-chart__head">
        <span className="ctl-chart__title">{chart.title}</span>
        <span className="ctl-chart__legend">
          {chart.series.map((s) => {
            const last = s.points[s.points.length - 1] ?? 0;
            return (
              <span key={s.label} className="ctl-chart__legend-item">
                <i style={{ background: SERIES_COLORS[s.color ?? 'blue'] }} /> {s.label}:{' '}
                {formatValue(last, chart.format, true)}
              </span>
            );
          })}
        </span>
      </div>
      <svg
        className="ctl-lines"
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={chart.title}
        preserveAspectRatio="none"
      >
        {/* Negativbereich rot hinterlegen — macht eine Liquiditätslücke sofort sichtbar */}
        {min < 0 ? (
          <rect x={0} y={zeroY} width={w} height={h - zeroY} className="ctl-lines__negative" />
        ) : null}
        <line x1={0} x2={w} y1={zeroY} y2={zeroY} className="ctl-lines__zero" />
        {chart.series.map((s) => {
          const color = SERIES_COLORS[s.color ?? 'blue']!;
          const path = s.points
            .map(
              (v, i) => `${i === 0 ? 'M' : 'L'}${(pad + i * step).toFixed(1)},${y(v).toFixed(1)}`,
            )
            .join(' ');
          return (
            <g key={s.label}>
              <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
              {s.points.map((v, i) => (
                <circle
                  key={i}
                  cx={pad + i * step}
                  cy={y(v)}
                  r="3"
                  fill={v < 0 ? SERIES_COLORS.red : color}
                >
                  <title>{`${axis[i] ?? ''}: ${formatValue(v, chart.format)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      <div className="ctl-lines__labels">
        {axis.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function InsightRow({ insight }: { insight: Insight }) {
  const meta = SEVERITY_META[insight.severity];
  const Icon = meta.icon;
  return (
    <div className={`ctl-insight ${meta.className}`}>
      <Icon size={18} aria-hidden className="ctl-insight__icon" />
      <div className="ctl-insight__body">
        <div className="ctl-insight__title">{insight.title}</div>
        <div className="ctl-insight__detail">{insight.detail}</div>
      </div>
      {insight.action ? (
        <Link to={insight.action.to} className="ctl-insight__action">
          {insight.action.label} →
        </Link>
      ) : null}
    </div>
  );
}

export default function ControllingPage() {
  const [monthCount, setMonthCount] = useState(12);
  const [drillKpi, setDrillKpi] = useState<Kpi | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const data = useQuery({
    queryKey: ['controlling', monthCount],
    queryFn: () => api<ControllingResponse>(`/api/v1/controlling?months=${monthCount}`),
  });

  const drillRows = useQuery({
    queryKey: ['controlling', 'drill', drillKpi?.key, monthCount],
    enabled: drillKpi?.drilldown !== undefined,
    queryFn: () =>
      api<RowsResult>('/api/v1/analytics/rows', {
        method: 'POST',
        body: {
          source: drillKpi!.drilldown!.source,
          ...(drillKpi!.drilldown!.filters ? { filters: drillKpi!.drilldown!.filters } : {}),
          limit: 100,
        },
      }),
  });

  async function downloadPdf(): Promise<void> {
    setPdfBusy(true);
    try {
      const tenant = tenantStore.get();
      const response = await fetch(`/api/v1/controlling/pdf?months=${monthCount}`, {
        credentials: 'include',
        headers: tenant !== null ? { 'x-tenant': tenant } : {},
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `Controlling-Cockpit-${new Date().toISOString().slice(0, 10)}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setPdfBusy(false);
    }
  }

  const months = data.data?.months ?? [];
  const sections = data.data?.sections ?? [];
  const heroKpis = sections.flatMap((s) => s.kpis.filter((k) => k.hero));
  const insights = sections
    .flatMap((s) => s.insights ?? [])
    .sort((a, b) => SEVERITY_META[a.severity].order - SEVERITY_META[b.severity].order);
  const domains = [...new Set(sections.map((s) => s.domain))];

  const today = new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="page page--wide">
      <div className="ctl-head">
        <div>
          <h1 className="page__title">Controlling</h1>
          <p className="page__lead">
            Zahlen, Trends und Handlungsfelder auf einen Blick — Stand {today}.
          </p>
        </div>
        <div className="ctl-head__actions">
          <Select
            aria-label="Zeitraum"
            value={String(monthCount)}
            onChange={(event) => setMonthCount(Number(event.target.value))}
            style={{ width: 'auto' }}
          >
            <option value="6">Letzte 6 Monate</option>
            <option value="12">Letzte 12 Monate</option>
            <option value="24">Letzte 24 Monate</option>
          </Select>
          <Button
            variant="subtle"
            disabled={sections.length === 0 || pdfBusy}
            loading={pdfBusy}
            onClick={() => void downloadPdf()}
          >
            <FileDown size={15} aria-hidden /> Als PDF
          </Button>
        </div>
      </div>

      {data.isLoading ? (
        <Card>
          <SkeletonText lines={6} />
        </Card>
      ) : sections.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Gauge size={28} aria-hidden />}
            title="Noch keine Controlling-Daten"
            description="Sobald Module aktiv sind und Daten enthalten, entsteht hier das Management-Cockpit."
          />
        </Card>
      ) : (
        <>
          {heroKpis.length > 0 ? (
            <div className="ctl-hero">
              {heroKpis.map((kpi) => (
                <HeroCard key={kpi.key} kpi={kpi} onDrill={setDrillKpi} />
              ))}
            </div>
          ) : null}

          {insights.length > 0 ? (
            <Card>
              <div className="ctl-section-title">
                <Gauge size={17} aria-hidden /> Optimierungspotenzial
                <span className="ctl-section-title__count">{insights.length}</span>
              </div>
              <div className="ctl-insights">
                {insights.map((insight, i) => (
                  <InsightRow key={i} insight={insight} />
                ))}
              </div>
            </Card>
          ) : null}

          {domains.map((domain) => {
            const meta = DOMAIN_META[domain];
            const Icon = meta.icon;
            const domainSections = sections.filter((s) => s.domain === domain);
            return (
              <div key={domain} className="ctl-domain">
                <div className="ctl-section-title">
                  <Icon size={17} aria-hidden /> {meta.label}
                </div>
                <div className="ctl-domain__grid">
                  {domainSections.map((section) => (
                    <Card key={section.key}>
                      {domainSections.length > 1 ? (
                        <div className="ctl-card-title">{section.title}</div>
                      ) : null}
                      <div className="ctl-kpis">
                        {section.kpis.map((kpi) => {
                          const clickable = kpi.drilldown !== undefined;
                          return (
                            <div
                              key={kpi.key}
                              className={`ctl-kpi${clickable ? ' ctl-kpi--clickable' : ''}`}
                              {...(clickable
                                ? {
                                    role: 'button' as const,
                                    tabIndex: 0,
                                    title: 'Datensätze anzeigen',
                                    onClick: () => setDrillKpi(kpi),
                                    onKeyDown: (e: React.KeyboardEvent) => {
                                      if (e.key === 'Enter' || e.key === ' ') setDrillKpi(kpi);
                                    },
                                  }
                                : {})}
                            >
                              <div className="ctl-kpi__value">
                                {formatValue(kpi.value, kpi.format, true)}
                                <Delta kpi={kpi} />
                                {clickable ? <span className="ctl-drill-hint">↳</span> : null}
                              </div>
                              <div className="ctl-kpi__label">{kpi.label}</div>
                              {kpi.hint ? <div className="ctl-kpi__hint">{kpi.hint}</div> : null}
                            </div>
                          );
                        })}
                      </div>
                      {(section.charts ?? []).map((chart) =>
                        chart.kind === 'lines' ? (
                          <LinesChart key={chart.key} chart={chart} months={months} />
                        ) : (
                          <BarsChart key={chart.key} chart={chart} months={months} />
                        ),
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      <Dialog
        open={drillKpi !== null}
        onClose={() => setDrillKpi(null)}
        wide
        aria-label="Datensätze hinter der Kennzahl"
      >
        <div className="ctl-drill">
          <div className="ctl-drill__head">
            <h2>{drillKpi?.label}</h2>
            <button
              type="button"
              className="ctl-drill__close"
              aria-label="Schließen"
              onClick={() => setDrillKpi(null)}
            >
              <X size={18} aria-hidden />
            </button>
          </div>
          {drillRows.isLoading ? (
            <SkeletonTable rows={6} />
          ) : drillRows.isError ? (
            <p className="ctl-drill__empty">Datensätze konnten nicht geladen werden.</p>
          ) : drillRows.data && drillRows.data.rows.length > 0 ? (
            <div className="ctl-drill__wrap">
              <table className="au-table">
                <thead>
                  <tr>
                    {drillRows.data.columns.map((column) => (
                      <th key={column.key} className={column.dimension ? undefined : 'ctl-num'}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drillRows.data.rows.map((row, index) => (
                    <tr key={index}>
                      {drillRows.data.columns.map((column) => (
                        <td key={column.key} className={column.dimension ? undefined : 'ctl-num'}>
                          {formatCell(column, row[column.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="ctl-drill__foot">
                {drillRows.data.truncated
                  ? 'Nur die ersten 100 Datensätze angezeigt.'
                  : `${drillRows.data.rows.length} Datensatz/-sätze`}
              </div>
            </div>
          ) : (
            <p className="ctl-drill__empty">Keine Datensätze hinter dieser Zahl.</p>
          )}
        </div>
      </Dialog>
    </div>
  );
}
